/**
 * ListManagerView.tsx - Main List Manager UI component
 * 
 * CHANGE HISTORY:
 * - 251130_1628 danielbeer: Created main ListManager view component
 * 
 * This component provides a complete UI for managing lists with:
 * - List selection and filtering
 * - Hierarchical editing with Outlinr
 * - Import/export functionality
 * - Split pane layout with resizable panels
 * - List information display
 * 
 * ARCHITECTURE:
 * - Uses Organizr APIs exclusively (no direct fetch calls)
 * - Delegates state management to useListManager hook
 * - Integrates with Outlinr for hierarchical editing
 * - Reusable across any app
 */

import React, { useState, useRef, useEffect } from 'react';
import { OutlinerView } from '@opcentral/outlinr';
import { SplitPanel } from '@opcentral/panelr';
import { AggregatrDatasetTool } from '@opcentral/aggregatr';
import { ListPicker } from './ListPicker';
import { ListInfo } from './ListInfo';
import { useListManager } from '../hooks/useListManager';
import type { OrganizrManager } from '../OrganizrManager';
import './ListManagerView.css';

export interface ListManagerViewProps {
  organizr: OrganizrManager;
  onError?: (error: string) => void;
  showInfoPanel?: boolean;
}

/**
 * Complete List Manager UI
 * 
 * @param props - Component props
 * @returns List manager interface
 * 
 * @example
 * import { getOrganizr } from '@opcentral/organizr';
 * import { ListManagerView } from '@opcentral/organizr';
 * 
 * const organizr = getOrganizr();
 * 
 * <ListManagerView
 *   organizr={organizr}
 *   onError={(err) => console.error(err)}
 * />
 */
export const ListManagerView: React.FC<ListManagerViewProps> = ({
  organizr,
  onError,
  showInfoPanel: initialShowInfo = true
}) => {
  // Use list manager hook for state
  const listManager = useListManager({
    organizr,
    autoLoadRegistry: true,
    onError
  });
  
  // UI state
  const [showInfo, setShowInfo] = useState(initialShowInfo);
  const [showMenu, setShowMenu] = useState(false);
  
  const menuRef = useRef<HTMLDivElement>(null);
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowMenu(false);
      }
    };
    
    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showMenu]);
  
  // Export list
  const handleExport = () => {
    try {
      const json = listManager.exportList();
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${listManager.currentList?.id || 'list'}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setShowMenu(false);
    } catch (err) {
      if (onError) {
        onError(`Export failed: ${err}`);
      }
    }
  };
  
  // Import list
  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const content = e.target?.result as string;
          listManager.importList(content);
          setShowMenu(false);
        };
        reader.readAsText(file);
      }
    };
    input.click();
  };
  
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--surface-base)', color: 'var(--text-primary)' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem 1rem', borderBottom: '1px solid var(--border-default)', background: 'var(--surface-raised)' }}>
        <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
          📋 List Manager
        </h2>
        
        {/* Save/Revert Buttons (when changes exist) */}
        {listManager.hasUnsavedChanges && listManager.currentList?.type === 'fixed' && (
          <div style={{ display: 'flex', gap: '0.5rem', marginLeft: 'auto', marginRight: '0.5rem' }}>
            <button
              onClick={() => listManager.revertChanges()}
              className="list-manager-actions-btn"
              title="Discard changes"
            >
              ↶ Revert
            </button>
            <button
              onClick={() => listManager.saveList()}
              style={{ padding: '0.25rem 0.75rem', borderRadius: '0.375rem', border: '1px solid var(--color-primary)', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500, background: 'var(--color-primary)', color: 'white' }}
              title="Save changes"
            >
              💾 Save
            </button>
          </div>
        )}
        
        {/* Actions Menu */}
        <div style={{ position: 'relative' }} ref={menuRef}>
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="list-manager-actions-btn"
          >
            ⋮ Actions
          </button>
          
          {showMenu && (
            <div className="list-manager-actions-menu">
              <button
                onClick={() => { listManager.saveList(); setShowMenu(false); }}
                disabled={!listManager.currentList || listManager.loading}
                className="list-manager-menu-item"
              >
                💾 Save List
              </button>
              
              <button
                onClick={handleExport}
                disabled={!listManager.currentList}
                className="list-manager-menu-item"
              >
                📥 Export JSON
              </button>
              
              <button
                onClick={handleImport}
                className="list-manager-menu-item"
              >
                📤 Import JSON
              </button>
              
              <div className="list-manager-menu-divider" />
              
              <button
                onClick={() => { listManager.loadListRegistry(); setShowMenu(false); }}
                className="list-manager-menu-item"
              >
                🔄 Refresh Lists
              </button>
            </div>
          )}
        </div>
      </div>
      
      {/* Error Display */}
      {listManager.error && (
        <div className="list-manager-error">
          <span>⚠️ {listManager.error}</span>
          <button
            onClick={listManager.clearError}
            className="list-manager-error-close"
          >
            ✕
          </button>
        </div>
      )}
      
      {/* Loading State */}
      {listManager.loading && (
        <div className="list-manager-loading">
          Loading...
        </div>
      )}
      
      {/* Main Content - Split Pane Layout using Panelr */}
      {!listManager.loading && (
        <div className="list-manager-content">
          <SplitPanel
            direction="horizontal"
            defaultSize={30}
            minSize={20}
            maxSize={50}
            persistent={true}
            storageKey="list-manager-left-pane"
          >
            {/* Left Pane: List Picker + Info */}
            <div className="list-manager-left-pane" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              {showInfo && listManager.currentList ? (
                <SplitPanel
                  direction="vertical"
                  defaultSize={60}
                  minSize={40}
                  maxSize={80}
                  persistent={true}
                  storageKey="list-manager-info-pane"
                >
                  {/* Upper: List Picker */}
                  <div className="list-manager-picker-section">
                    <ListPicker
                      lists={listManager.availableLists}
                      selectedListId={listManager.selectedListId}
                      onSelectList={listManager.selectList}
                      loading={listManager.loading}
                    />
                  </div>
                  
                  {/* Lower: Info Panel with Header */}
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    {/* Info Panel Header */}
                    <div className="list-manager-footer">
                      <button
                        onClick={() => setShowInfo(!showInfo)}
                        title="Hide list information"
                        className="list-manager-info-toggle"
                      >
                        ▼
                      </button>
                      
                      <div className="list-manager-status">
                        <span className="list-manager-status-item">
                          {listManager.currentList.children?.length || 0} items
                        </span>
                        <span>•</span>
                        <span className="list-manager-status-item">
                          {listManager.currentList.type === 'dynamic' ? '🔄 Dynamic' : '📋 Static'}
                        </span>
                      </div>
                    </div>
                    
                    {/* Info Panel Content */}
                    <div className="list-manager-info-panel" style={{ flex: 1, overflow: 'auto' }}>
                      <ListInfo list={listManager.currentList} compact={true} />
                    </div>
                  </div>
                </SplitPanel>
              ) : (
                <>
                  {/* List Picker - takes remaining space */}
                  <div className="list-manager-picker-section" style={{ flex: 1, overflow: 'hidden' }}>
                    <ListPicker
                      lists={listManager.availableLists}
                      selectedListId={listManager.selectedListId}
                      onSelectList={listManager.selectList}
                      loading={listManager.loading}
                    />
                  </div>
                  
                  {/* Collapsed Header - at bottom */}
                  {listManager.currentList && (
                    <div className="list-manager-footer">
                      <button
                        onClick={() => setShowInfo(!showInfo)}
                        title="Show list information"
                        className="list-manager-info-toggle"
                      >
                        ▲
                      </button>
                      
                      <div className="list-manager-status">
                        <span className="list-manager-status-item">
                          {listManager.currentList.children?.length || 0} items
                        </span>
                        <span>•</span>
                        <span className="list-manager-status-item">
                          {listManager.currentList.type === 'dynamic' ? '🔄 Dynamic' : '📋 Static'}
                        </span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            
            {/* Right Pane: Outlinr Editor or Dataset Manager */}
            <div className="list-manager-right-pane">
            {listManager.currentList ? (
              listManager.currentList.type === 'dynamic' ? (
                <AggregatrDatasetTool
                  appId="organizr"
                  onError={onError}
                />
              ) : (
                <OutlinerView
                  nodes={listManager.outlinerNodes}
                  onNodesChange={listManager.updateNodes}
                  readonly={false}
                  familyRules={true}
                />
              )
            ) : (
              <div className="list-manager-empty-state">
                Select a list to edit
              </div>
            )}
            </div>
          </SplitPanel>
        </div>
      )}
    </div>
  );
};

export default ListManagerView;
